/**
 * What `GET /v1/agent/status` said, for the person signed in now.
 *
 * A store and not a hook's own state because the door sits in every header,
 * and a fetch per header would be a fetch per screen. The first door to mount
 * asks; every door after reads the answer. Nothing here is persisted: a key
 * added to the server between two launches should light the door on the
 * second launch without a stale "off" in the way.
 *
 * `SessionGate` calls `reset` whenever the signed-in person changes: signing
 * out, signing in, and switching person. The answer belongs to the person it
 * was asked for, and `session-gate.tsx` says why.
 *
 * A failed read stays failed until then rather than retrying on every mount.
 * The door is a courtesy, and a server that cannot answer this is not one the
 * chat would reach either.
 */
import type { AgentStatus } from '@alluzo/shared';
import { create } from 'zustand';

import type { AgentStatusHeld } from '@/lib/agent-status';

type AgentStatusState = {
  held: AgentStatusHeld;
  /** A door is asking. Every other door waits for the answer. */
  begin: () => void;
  settle: (status: AgentStatus) => void;
  fail: () => void;
  /** The signed-in person changed. The next door to mount asks again. */
  reset: () => void;
};

export const useAgentStatusStore = create<AgentStatusState>()((set) => ({
  held: { kind: 'unknown' },
  begin: () => set({ held: { kind: 'loading' } }),
  settle: (status) => set({ held: { kind: 'known', status } }),
  fail: () => set({ held: { kind: 'failed' } }),
  reset: () => set({ held: { kind: 'unknown' } }),
}));
